import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { connectWS, disconnectWS, onEvent, onWSConnectionChange } from "../lib/ws";

const WebSocketContext = createContext(null);

export function WebSocketProvider({ children }) {
  const { user } = useAuth();
  const [wsConnected, setWsConnected] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("bansal_token");
    if (user && token) {
      connectWS(token);
    } else {
      disconnectWS();
    }
  }, [user]);

  useEffect(() => onWSConnectionChange(setWsConnected), []);

  /**
   * Subscribe to a WebSocket event type.
   * @param {string} type  e.g. "gate_pass", "purchase", "lookup"
   * @param {Function} cb  callback(event)
   * @returns {Function}   unsubscribe fn — return it from useEffect for cleanup
   */
  const subscribe = useCallback((type, cb) => {
    return onEvent(type, cb);
  }, []);

  return (
    <WebSocketContext.Provider value={{ subscribe, wsConnected }}>
      {children}
    </WebSocketContext.Provider>
  );
}

/** Subscribe to WS events with stable cleanup (safe to pass inline callbacks). */
export function useWSEvent(type, handler) {
  const { subscribe } = useWS();
  const handlerRef = React.useRef(handler);
  handlerRef.current = handler;

  useEffect(() => subscribe(type, (event) => handlerRef.current(event)), [subscribe, type]);
}

export function useWS() {
  const ctx = useContext(WebSocketContext);
  if (!ctx) throw new Error("useWS must be used inside <WebSocketProvider>");
  return ctx;
}
